"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import { MANGAL_HOUSES_EN } from "@ishvaram/domain-data/dosha-phrases";

interface DoshaDef {
  id: string;
  name_en: string;
  name_hi: string;
  symbol: string;
  planet: string;
  formation: string;
  impacts: string[];
  cancellation: string;
  remedy: string;
  link: string;
}

const DOSHAS: DoshaDef[] = [
  {
    id: "mangal",
    name_en: "Mangal Dosha (Kuja Dosha)",
    name_hi: "मांगलिक दोष",
    symbol: "🔴",
    planet: "Mars (Mangal)",
    formation: `Mars placed in the ${MANGAL_HOUSES_EN} house from the Lagna, with no benefic joining or aspecting it.`,
    impacts: ["Marriage timing & harmony", "Temperament & emotional friction", "Spouse health"],
    cancellation:
      "Cancelled if Mars is in its own sign in the 4th, in Capricorn in the 7th, or if the partner's chart carries the same placement.",
    remedy:
      "Chant Hanuman Chalisa, recite 'Om Kram Kreem Kroum Sah Bhaumaya Namah', offer red lentils (masoor daal) on Tuesdays.",
    link: "/mangal-dosha-calculator/",
  },
  {
    id: "kaal_sarp",
    name_en: "Kaal Sarp Dosha",
    name_hi: "कालसर्प दोष",
    symbol: "🐉",
    planet: "Rahu & Ketu",
    formation: "All seven primary planets hemmed between the Rahu-Ketu axis in the birth chart.",
    impacts: [
      "Sudden hurdles & delays",
      "Unsettled mental peace",
      "Struggle before sudden breakthrough",
    ],
    cancellation:
      "Partial Kaal Sarp (Anshik) occurs if even one planet exits the axis. Cancelled if Jupiter aspects Rahu/Ketu.",
    remedy:
      "Maha Mrityunjaya Japa, Shiva Abhishekam with milk/water on Mondays, Rahu-Ketu Shanti puja during Nag Panchami.",
    link: "/kaal-sarp-dosha/",
  },
  {
    id: "sade_sati",
    name_en: "Shani Sade Sati",
    name_hi: "साढ़े साती",
    symbol: "🪐",
    planet: "Saturn (Shani)",
    formation:
      "Saturn transiting through the 12th, 1st, and 2nd houses from the natal Moon sign (7.5 years).",
    impacts: [
      "Deep karmic lessons & maturity",
      "Career restructuring & discipline",
      "Financial realignment",
    ],
    cancellation:
      "Saturn is a yogakaraka for Taurus & Libra ascendants; benign if Moon is in friendly/own rashi (Capricorn/Aquarius).",
    remedy:
      "Light mustard oil lamp under Peepal tree on Saturdays, recite Shani Chalisa, engage in selfless seva for the elderly/laborers.",
    link: "/sade-sati/",
  },
  {
    id: "pitra",
    name_en: "Pitra Dosha",
    name_hi: "पितृ दोष",
    symbol: "☀️",
    planet: "Sun, Rahu, Saturn",
    formation:
      "Sun afflicted by Rahu, Ketu, or Saturn in the 9th house of Dharma, ancestral lineage, and father.",
    impacts: [
      "Obstacles in progeny & lineage",
      "Delayed family prosperity",
      "Frequent domestic strife",
    ],
    cancellation:
      "Neutralized if Jupiter strongly aspects the 9th house or 9th lord is powerfully placed in Kendra/Trikona.",
    remedy:
      "Perform Shraddha and Tarpan during Pitru Paksha, feed cows and crows, donate grains (wheat/jaggery) on Amavasya.",
    link: "/pitra-dosha/",
  },
  {
    id: "guru_chandal",
    name_en: "Guru Chandal Dosha",
    name_hi: "गुरु चांडाल दोष",
    symbol: "🟡",
    planet: "Jupiter & Rahu",
    formation: "Jupiter (Guru) and Rahu placed in the same house in close conjunction.",
    impacts: [
      "Conflict in belief systems",
      "Moral or educational challenges",
      "Difficulty finding trustworthy mentors",
    ],
    cancellation:
      "Cancelled if Jupiter is exalted in Cancer, in own sign (Sagittarius/Pisces), or at higher degree than Rahu.",
    remedy:
      "Offer yellow sweets/chana daal to Brahmins on Thursdays, recite Guru Mantra 'Om Gram Greem Groum Sah Gurave Namah'.",
    link: "/guru-chandal-dosha/",
  },
];

export default function DoshaDiagnosticStudio() {
  const [selectedId, setSelectedId] = useState<string>("mangal");

  const dosha = useMemo<DoshaDef>(() => {
    return DOSHAS.find((d) => d.id === selectedId) || DOSHAS[0];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `Please analyze ${dosha.name_en} (${dosha.name_hi}) in my Kundali. How can I check if it is active or cancelled, and what are the exact authentic Vedic remedies (upay)?`;
    try {
      safeSessionSet("seo_entry_intent", "dosha_remedy");
      safeSessionSet("seo_entry_path", "/doshas");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <DoshaSelector selectedId={selectedId} onSelect={setSelectedId} />
      <DoshaDetailsCard dosha={dosha} onConsult={handleConsult} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        Vedic Diagnostic Explorer
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Doshas, Cancellations &amp; Authentic Upay Studio
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Select any planetary dosha to see how it forms in a Janam Kundali, its classical
        cancellation conditions, and shastric remedies.
      </p>
    </div>
  );
}

function DoshaSelector({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {DOSHAS.map((d) => {
          const isSelected = d.id === selectedId;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onSelect(d.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="text-lg block mb-1">{d.symbol}</span>
              <span className="block text-body-small font-bold text-text-primary">
                {d.name_en.split(" ")[0]}
              </span>
              <span className="block text-caption text-text-muted">{d.name_hi}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DoshaDetailsCard({ dosha, onConsult }: { dosha: DoshaDef; onConsult: () => void }) {
  const chatHref = buildSEOAgentHref(getSEOIntentConfigForSource("/doshas"), "/doshas", "en");

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {dosha.symbol} {dosha.name_en}
            </h3>
            <span className="px-3 py-0.5 rounded-full text-caption font-bold bg-intent-brand/10 text-intent-brand">
              {dosha.planet}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Formation: <strong className="text-text-primary">{dosha.formation}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Check My Chart for {dosha.name_en.split(" ")[0]} ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-surface-raised border border-border-default">
          <p className="text-caption font-bold uppercase tracking-wider text-text-muted mb-2">
            Impacted Life Domains
          </p>
          <ul className="space-y-1.5 text-caption text-text-primary">
            {dosha.impacts.map((imp, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className="text-intent-brand">•</span>
                <span>{imp}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 rounded-xl bg-success/5 border border-success/20">
          <p className="text-caption font-bold uppercase tracking-wider text-success mb-2">
            Cancellation Rules (Parihara)
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{dosha.cancellation}</p>
        </div>

        <div className="p-4 rounded-xl bg-intent-brand/5 border border-intent-brand/20">
          <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-2">
            Authentic Vedic Upay (Remedies)
          </p>
          <p className="text-caption text-text-primary leading-relaxed">{dosha.remedy}</p>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          Need an exact mathematical calculation for your birth date and time?
        </span>
        <Link
          href={dosha.link}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Open Free {dosha.name_en.split(" ")[0]} Calculator →
        </Link>
      </div>
    </div>
  );
}
